package com.castly.admin.ui.home

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.api.HomeSectionApiClient
import com.castly.admin.ui.BannerEditor
import com.castly.admin.ui.CarouselEditor
import com.castly.admin.ui.ContinueWatchingSectionEditor
import com.castly.admin.ui.StaticSectionEditor
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminSectionDto
import com.castly.shared.dto.CreateSectionRequest
import com.castly.shared.dto.SectionTranslationData
import com.castly.shared.dto.UpdateSectionRequest
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.files.FileReader

/**
 * Home Section Editor Page (T067, T090)
 *
 * Features:
 * - Create/Edit sections of any type (carousel, banner, static)
 * - T090: Integrates CarouselEditor, BannerEditor, and StaticSectionEditor based on section type
 * - Handles section creation and updates
 * - Compresses banner images client-side before upload
 * - Provides navigation back to section list
 */
@Composable
fun HomeSectionEditorPage(
    sectionId: String?,
    initialSectionType: String? = null,
    apiClient: ApiClient,
    homeSectionApiClient: HomeSectionApiClient,
    onBack: () -> Unit = {},
    onBannerFilesChanged: (org.w3c.files.File?, org.w3c.files.File?) -> Unit = { _, _ -> },
    onUploadBannerImage: suspend (org.w3c.files.File, String, String) -> String? = { _, _, _ -> null },
    onSaveComplete: (String?) -> Unit = {}
) {
    val colors = AppTheme.colors

    // Form data holder - defined first so it can be used below
    data class FormData(
        val internalName: String = "",
        val vendorId: String? = null,
        val bannerImageEn: String? = null,
        val bannerImageAr: String? = null,
        val bannerLink: String? = null,
        val contentType: String? = null,
        val translations: List<SectionTranslationData>? = null
    )

    var section by remember { mutableStateOf<AdminSectionDto?>(null) }
    var loading by remember { mutableStateOf(sectionId != null) }
    var error by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }

    // T090: Section type selector for new sections
    var sectionType by remember { mutableStateOf(initialSectionType ?: section?.sectionType ?: "carousel") }

    // Form data state
    var formData by remember { mutableStateOf<FormData?>(null) }

    // Banner files state (to compress and upload before saving section)
    var bannerEnFile by remember { mutableStateOf<org.w3c.files.File?>(null) }
    var bannerArFile by remember { mutableStateOf<org.w3c.files.File?>(null) }

    // Upload progress state
    var uploadProgress by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    // Load existing section if editing
    LaunchedEffect(sectionId) {
        sectionId?.let { id ->
            loading = true
            try {
                val sections = homeSectionApiClient.getAllSections(includeDeleted = true)
                section = sections.find { it.id == id }
                if (section == null) {
                    error = "Section not found"
                } else {
                    sectionType = section!!.sectionType
                }
            } catch (e: Exception) {
                error = "Failed to load section: ${e.message}"
                console.error("Error loading section", e)
            } finally {
                loading = false
            }
        }
    }

    Div({
        style {
            maxWidth(1200.px)
            property("margin", "0 auto")
            themeTransition()
        }
    }) {
        // Error message
        error?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    color(Color(if (colors == LightColors) "#b91c1c" else "#fca5a5"))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Text(msg)
            }
        }

        // Loading state
        if (loading) {
            Div({
                style {
                    textAlign("center")
                    padding(40.px)
                    color(Color(colors.textMuted))
                }
            }) {
                Text("Loading section...")
            }
        } else {
            // T090: Section type selector (only for new sections and when initialSectionType is not provided)
            if (sectionId == null && initialSectionType == null) {
                Div({
                    style {
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.lg)
                        padding(Spacing.xxl)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        marginBottom(Spacing.xl)
                        themeTransition()
                    }
                }) {
                    H3({ style { marginTop(0.px); marginBottom(Spacing.lg); color(Color(colors.text)) } }) {
                        Text("Select Section Type")
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            property("gap", "${Spacing.md}")
                        }
                    }) {
                        // Carousel option
                        Button({
                            onClick { sectionType = "carousel" }
                            style {
                                flex("1")
                                padding(Spacing.lg)
                                backgroundColor(if (sectionType == "carousel") Color(colors.primary) else Color(colors.surface))
                                color(if (sectionType == "carousel") Color("#ffffff") else Color(colors.text))
                                border(2.px, LineStyle.Solid, if (sectionType == "carousel") Color(colors.primary) else Color(colors.border))
                                borderRadius(BorderRadius.lg)
                                cursor("pointer")
                                textAlign("left")
                            }
                        }) {
                            Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                Text("Carousel")
                            }
                            Div({ style { fontSize(Typography.base); property("opacity", "0.9") } }) {
                                Text("Horizontal scrolling content list")
                            }
                        }

                        // Banner option
                        Button({
                            onClick { sectionType = "banner" }
                            style {
                                flex("1")
                                padding(Spacing.lg)
                                backgroundColor(if (sectionType == "banner") Color(colors.primary) else Color(colors.surface))
                                color(if (sectionType == "banner") Color("#ffffff") else Color(colors.text))
                                border(2.px, LineStyle.Solid, if (sectionType == "banner") Color(colors.primary) else Color(colors.border))
                                borderRadius(BorderRadius.lg)
                                cursor("pointer")
                                textAlign("left")
                            }
                        }) {
                            Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                Text("Banner")
                            }
                            Div({ style { fontSize(Typography.base); property("opacity", "0.9") } }) {
                                Text("Full-width promotional image")
                            }
                        }

                        // Static option
                        Button({
                            onClick { sectionType = "static" }
                            style {
                                flex("1")
                                padding(Spacing.lg)
                                backgroundColor(if (sectionType == "static") Color(colors.primary) else Color(colors.surface))
                                color(if (sectionType == "static") Color("#ffffff") else Color(colors.text))
                                border(2.px, LineStyle.Solid, if (sectionType == "static") Color(colors.primary) else Color(colors.border))
                                borderRadius(BorderRadius.lg)
                                cursor("pointer")
                                textAlign("left")
                            }
                        }) {
                            Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                Text("Static")
                            }
                            Div({ style { fontSize(Typography.base); property("opacity", "0.9") } }) {
                                Text("Vendor-specific placeholder")
                            }
                        }
                    }

                    // Second row: Episode Carousel and Continue Watching types
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            property("gap", "${Spacing.md}")
                            marginTop(Spacing.md)
                        }
                    }) {
                        // Episode Carousel option
                        Button({
                            onClick { sectionType = "episode_carousel" }
                            style {
                                flex("1")
                                padding(Spacing.lg)
                                backgroundColor(if (sectionType == "episode_carousel") Color(colors.primary) else Color(colors.surface))
                                color(if (sectionType == "episode_carousel") Color("#ffffff") else Color(colors.text))
                                border(2.px, LineStyle.Solid, if (sectionType == "episode_carousel") Color(colors.primary) else Color(colors.border))
                                borderRadius(BorderRadius.lg)
                                cursor("pointer")
                                textAlign("left")
                            }
                        }) {
                            Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                Text("Episode Carousel")
                            }
                            Div({ style { fontSize(Typography.base); property("opacity", "0.9") } }) {
                                Text("Scrolling list of hand-picked episodes")
                            }
                        }

                        // Continue Watching option
                        Button({
                            onClick { sectionType = "continue_watching" }
                            style {
                                flex("1")
                                padding(Spacing.lg)
                                backgroundColor(if (sectionType == "continue_watching") Color(colors.primary) else Color(colors.surface))
                                color(if (sectionType == "continue_watching") Color("#ffffff") else Color(colors.text))
                                border(2.px, LineStyle.Solid, if (sectionType == "continue_watching") Color(colors.primary) else Color(colors.border))
                                borderRadius(BorderRadius.lg)
                                cursor("pointer")
                                textAlign("left")
                            }
                        }) {
                            Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                Text("Continue Watching")
                            }
                            Div({ style { fontSize(Typography.base); property("opacity", "0.9") } }) {
                                Text("Single hero card (per user)")
                            }
                        }

                        // Continue Watching Carousel option
                        Button({
                            onClick { sectionType = "continue_watching_carousel" }
                            style {
                                flex("1")
                                padding(Spacing.lg)
                                backgroundColor(if (sectionType == "continue_watching_carousel") Color(colors.primary) else Color(colors.surface))
                                color(if (sectionType == "continue_watching_carousel") Color("#ffffff") else Color(colors.text))
                                border(2.px, LineStyle.Solid, if (sectionType == "continue_watching_carousel") Color(colors.primary) else Color(colors.border))
                                borderRadius(BorderRadius.lg)
                                cursor("pointer")
                                textAlign("left")
                            }
                        }) {
                            Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                Text("Continue Watching Carousel")
                            }
                            Div({ style { fontSize(Typography.base); property("opacity", "0.9") } }) {
                                Text("Scrolling progress list (per user)")
                            }
                        }
                    }
                }
            }

            // T090: Render appropriate editor based on section type
            when (sectionType) {
                "carousel", "episode_carousel" -> {
                    CarouselEditor(
                        section = section,
                        apiClient = apiClient,
                        homeSectionApiClient = homeSectionApiClient,
                        episodeMode = sectionType == "episode_carousel",
                        onDataChanged = { internalName, contentType, translations ->
                            formData = FormData(
                                internalName = internalName,
                                contentType = contentType,
                                translations = translations
                            )
                        }
                    )
                }
                "banner" -> {
                    BannerEditor(
                        section = section,
                        homeSectionApiClient = homeSectionApiClient,
                        onDataChanged = { internalName, imageEn, imageAr, link ->
                            formData = FormData(
                                internalName = internalName,
                                bannerImageEn = imageEn,
                                bannerImageAr = imageAr,
                                bannerLink = link
                            )
                        },
                        onFilesChanged = { enFile, arFile ->
                            bannerEnFile = enFile
                            bannerArFile = arFile
                            onBannerFilesChanged(enFile, arFile)
                        }
                    )
                }
                "static" -> {
                    StaticSectionEditor(
                        section = section,
                        onUpdate = { internalName, vendorId ->
                            formData = FormData(
                                internalName = internalName,
                                vendorId = vendorId
                            )
                        },
                        onCheckVendorId = { vendorId ->
                            val result = homeSectionApiClient.checkVendorId(vendorId, excludeId = sectionId)
                            if (!result.available) {
                                val deletedAt = result.conflictDeletedAt
                                if (deletedAt != null) {
                                    val dateStr = deletedAt.substring(0, 10)
                                    "Vendor ID previously used by deleted section '${result.conflictSectionName}' (deleted on $dateStr). Please choose a different vendor ID."
                                } else {
                                    "Vendor ID already in use by active section '${result.conflictSectionName}'."
                                }
                            } else {
                                null
                            }
                        }
                    )
                }
                "continue_watching", "continue_watching_carousel" -> {
                    ContinueWatchingSectionEditor(
                        section = section,
                        sectionType = sectionType,
                        onDataChanged = { internalName, translations ->
                            formData = FormData(
                                internalName = internalName,
                                translations = translations
                            )
                        }
                    )
                }
            }

            // Save button at the bottom
            Div({
                style {
                    marginTop(Spacing.xxl)
                    padding(Spacing.lg)
                    backgroundColor(Color(colors.surfaceHover))
                    borderRadius(BorderRadius.sm)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.End)
                    property("gap", "${Spacing.sm}")
                    alignItems(AlignItems.Center)
                    themeTransition()
                }
            }) {
                // Progress message visible while saving
                uploadProgress?.let { progress ->
                    Span({ style { color(Color(colors.primary)); marginRight(Spacing.md); fontSize(Typography.base) } }) {
                        Text(progress)
                    }
                }
                Button({
                    onClick { onBack() }
                    style {
                        padding(10.px, Spacing.xl)
                        backgroundColor(Color(colors.textMuted))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.sm)
                        cursor("pointer")
                    }
                }) {
                    Text("Cancel")
                }

                Button({
                    onClick {
                        scope.launch {
                            val data = formData
                            if (data == null || data.internalName.isBlank()) {
                                error = "Please fill in all required fields"
                                return@launch
                            }

                            val enFile = bannerEnFile
                            val arFile = bannerArFile

                            // Validate: new banner sections require at least one image
                            if (sectionType == "banner" && sectionId == null && enFile == null && arFile == null) {
                                error = "Please select at least one banner image (English or Arabic)"
                                return@launch
                            }

                            saving = true
                            error = null
                            uploadProgress = null

                            try {
                                var finalBannerImageEn = data.bannerImageEn
                                var finalBannerImageAr = data.bannerImageAr

                                // Banner with new image files: compress → (create if new) → upload → update
                                if (sectionType == "banner" && (enFile != null || arFile != null)) {
                                    // Determine target section ID (create first if this is a new section)
                                    val targetSectionId: String
                                    if (sectionId == null) {
                                        uploadProgress = "Creating section..."
                                        val createRequest = CreateSectionRequest(
                                            sectionType = sectionType,
                                            internalName = data.internalName,
                                            vendorId = data.vendorId,
                                            bannerImageEn = null,
                                            bannerImageAr = null,
                                            bannerLink = data.bannerLink,
                                            contentType = data.contentType,
                                            translations = data.translations
                                        )
                                        val created = homeSectionApiClient.createSection(createRequest)
                                        targetSectionId = created.id
                                    } else {
                                        targetSectionId = sectionId
                                    }

                                    // Compress and upload English banner
                                    if (enFile != null) {
                                        uploadProgress = "Compressing English banner..."
                                        val compressed = compressImage(enFile)
                                        uploadProgress = "Uploading English banner..."
                                        val url = onUploadBannerImage(compressed, targetSectionId, "eng")
                                            ?: throw Exception("Failed to upload English banner")
                                        finalBannerImageEn = url
                                    }

                                    // Compress and upload Arabic banner
                                    if (arFile != null) {
                                        uploadProgress = "Compressing Arabic banner..."
                                        val compressed = compressImage(arFile)
                                        uploadProgress = "Uploading Arabic banner..."
                                        val url = onUploadBannerImage(compressed, targetSectionId, "ara")
                                            ?: throw Exception("Failed to upload Arabic banner")
                                        finalBannerImageAr = url
                                    }

                                    // Update section with final image URLs
                                    uploadProgress = "Saving section..."
                                    homeSectionApiClient.updateSection(
                                        targetSectionId,
                                        UpdateSectionRequest(
                                            internalName = data.internalName,
                                            vendorId = data.vendorId,
                                            bannerImageEn = finalBannerImageEn,
                                            bannerImageAr = finalBannerImageAr,
                                            bannerLink = data.bannerLink,
                                            contentType = data.contentType,
                                            translations = data.translations
                                        )
                                    )
                                    uploadProgress = null
                                    onSaveComplete(targetSectionId)
                                    return@launch
                                }

                                // Non-banner or banner with no new files selected (edit preserving existing URLs)
                                uploadProgress = "Saving section..."
                                val savedId = if (sectionId == null) {
                                    val created = homeSectionApiClient.createSection(
                                        CreateSectionRequest(
                                            sectionType = sectionType,
                                            internalName = data.internalName,
                                            vendorId = data.vendorId,
                                            bannerImageEn = finalBannerImageEn,
                                            bannerImageAr = finalBannerImageAr,
                                            bannerLink = data.bannerLink,
                                            contentType = data.contentType,
                                            translations = data.translations
                                        )
                                    )
                                    created.id
                                } else {
                                    homeSectionApiClient.updateSection(
                                        sectionId,
                                        UpdateSectionRequest(
                                            internalName = data.internalName,
                                            vendorId = data.vendorId,
                                            bannerImageEn = finalBannerImageEn,
                                            bannerImageAr = finalBannerImageAr,
                                            bannerLink = data.bannerLink,
                                            contentType = data.contentType,
                                            translations = data.translations
                                        )
                                    )
                                    sectionId
                                }
                                uploadProgress = null
                                onSaveComplete(savedId)
                            } catch (e: Exception) {
                                error = "Failed to save section: ${e.message}"
                                console.error("Error saving section", e)
                            } finally {
                                saving = false
                                uploadProgress = null
                            }
                        }
                    }
                    if (saving) {
                        attr("disabled", "disabled")
                    }
                    style {
                        padding(10.px, Spacing.xl)
                        backgroundColor(if (saving) Color(colors.textMuted) else Color(colors.primary))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.sm)
                        cursor(if (saving) "not-allowed" else "pointer")
                    }
                }) {
                    Text(uploadProgress ?: if (saving) "Saving..." else "Save Section")
                }
            }
        }
    }
}

/**
 * Compress an image file using the HTML5 Canvas API.
 *
 * Resizes to at most [maxWidth] pixels wide (preserving aspect ratio) and
 * re-encodes as JPEG at the given [quality] (0.0–1.0).
 * Falls back to the original file if anything goes wrong.
 */
private suspend fun compressImage(
    file: org.w3c.files.File,
    maxWidth: Int = 1200,
    quality: Double = 0.82
): org.w3c.files.File = suspendCoroutine { continuation ->
    val reader = FileReader()
    reader.onload = { _ ->
        val dataUrl = reader.result as String
        val img = document.createElement("img")
        img.asDynamic().onload = {
            try {
                val htmlImg = img.unsafeCast<HTMLImageElement>()
                val canvas = document.createElement("canvas").unsafeCast<HTMLCanvasElement>()
                var w = htmlImg.naturalWidth
                var h = htmlImg.naturalHeight
                if (w > maxWidth) {
                    h = (h.toDouble() * maxWidth / w).toInt()
                    w = maxWidth
                }
                canvas.width = w
                canvas.height = h
                val ctx = canvas.getContext("2d").unsafeCast<CanvasRenderingContext2D>()
                ctx.drawImage(htmlImg, 0.0, 0.0, w.toDouble(), h.toDouble())
                val origName = file.name
                canvas.toBlob({ blob ->
                    if (blob != null) {
                        @Suppress("UnsafeCastFromDynamic")
                        val compressed = js("new File([blob], origName, { type: 'image/jpeg' })")
                            .unsafeCast<org.w3c.files.File>()
                        continuation.resume(compressed)
                    } else {
                        continuation.resume(file)
                    }
                }, "image/jpeg", quality)
            } catch (e: Throwable) {
                continuation.resume(file)
            }
        }
        img.asDynamic().onerror = { continuation.resume(file) }
        img.asDynamic().src = dataUrl
        null
    }
    reader.onerror = { _ -> continuation.resume(file) }
    reader.readAsDataURL(file)
}
